import { ofType } from 'redux-observable';
import { filter, map, mapTo, switchMap, take } from 'rxjs/operators';
import { of, merge, timer, from } from 'rxjs';

import {
   EMULATE_EXERCISE_FOR_TUTORIAL,
   exerciseChallengeReady,
   exerciseReady,
   INIT,
   PREPARE_NEXT_CHALLENGE,
   PREPARE_NEXT_SESSION,
   SET_STAGE,
   START_NEXT_CHALLENGE,
} from '../actions';
import { exerciseStages } from '../../shared/constants';
import { combineExerciseEpics } from '../combineExerciseEpics';

import { getRandomArrayElement } from '../../util/getRandomArrayElement';
import { getCurrentLevel } from '../getCurrentLevel';
import {
   loadLoopCategoryCount,
   replaceQueueWithNextLevel,
   setCurrentLoopFromQueue,
   setCurrentLoop,
   setInstruments,
   setLoopMode,
   setView,
   SET_CURRENT_LOOP_FROM_QUEUE,
} from '../../interfaces/sequencer/actions';
import { randomNumberInRange } from '../interval/util';
import { getSessionsAndAverageScore } from '../getSessionsAndAverageScore';
import { resolveLoopQueueItems } from '../resolveLoopQueueItems';
import {
   sequencerInstrumentControlTypes,
   sequencerViewTypes,
} from '../../interfaces/sequencer/constants';
import { createParameter } from '../createParameter';

// queue: [] <-- added to as appropriate
// nextLevelQueue: [] <- only relevent if they have enough sessions that they could level up next session

//* On init, add <challengesPerSession> loops to queue
//*    ---- load 1? all? of these (load n more on startNextChallenge?)
//
//* on prepare challenge, update current from queue, make sure loop matches current exercise and level, and wait for loop to be loaded...
//* load if mismatched and/or not loading/loaded
//*  update lastaccessed value on cached loop
//
//* on startNextChallenge/prepareNextChallenge/submitanswer? refresh queue to contain <challengesPerSession> loops
//*    ---- load n of these
//
//* on startNextChallenge/prepareNextChallenge/submitanswer? IFF their sessions at currentLevel >= required sessions to level up - 1, then load 1? item into nextLevelQueue
//*    ---- load n of these
//
//* on level up -> if nextLevelQueue, replace queue with nextLevelQueue
//
//* on prepare session, make sure <challengesPerSession> items are loaded into queue... this should only happen if leveled up... maybe not needed
//
//* clean up stored loops cache as progressing... max 5? 10?... don't clean up the one loaded from nextLevel... clean that up if nextLevelQueue is ceared and not sent to queue

const getLevel = (state) =>
   state.activeExercise.exerciseDocument.levels[getCurrentLevel(state) - 1];

const selectSamples = (loop, trackCount) => {
   const sortedSampleEntries = Object.entries(loop.samples).sort(
      (sampleA, sampleB) => (sampleA[1].layer > sampleB[1].layer ? 1 : 0)
   );

   const maxLayer =
      sortedSampleEntries[sortedSampleEntries.length - 1][1].layer;

   const selectedSamples = {};
   const layerCounts = {};
   let selectedCount = 0;
   let currentLayerOrder = 1;

   while (selectedCount < trackCount && currentLayerOrder <= maxLayer) {
      const fromLayerOrder = sortedSampleEntries.filter(
         ([, { layerOrder }]) => layerOrder === currentLayerOrder
      );

      const fromIncompleteLayers = fromLayerOrder.filter(
         ([, { layer, chooseMax }]) =>
            layerCounts[layer] == null || layerCounts[layer] < chooseMax
      );

      const fromUnselected = fromIncompleteLayers.filter(
         ([sampleId]) => selectedSamples[sampleId] == null
      );

      if (fromUnselected.length > 0) {
         selectedCount += 1;

         const [chosenSampleId, chosenSample] =
            getRandomArrayElement(fromUnselected);

         selectedSamples[chosenSampleId] = true;

         if (layerCounts[chosenSample.layer] == null) {
            layerCounts[chosenSample.layer] = 1;
         } else {
            layerCounts[chosenSample.layer] += 1;
         }
      } else {
         currentLayerOrder += 1;
      }
   }

   return sortedSampleEntries
      .filter(([id]) => selectedSamples[id])
      .map(([id]) => id);
};

const createMixer = (loop, level) => {
   const sampleIds = selectSamples(loop, level.trackCount);

   return sampleIds.map((id) => ({
      source: id,
      name: loop.samples[id].name,
      referenceMixerTrack: {
         pan: createParameter({
            exerciseParameterConfig: level.enabledControls.pan,
            defaultValue: 0,
            randomize: loop.samples[id].pannable,
            bipolar: true,
         }),
         gain: createParameter({
            exerciseParameterConfig: level.enabledControls.gain,
            defaultValue: 0.6,
            randomize: true,
         }),
         effects: [],
      },

      mixerTrack: {
         pan: createParameter({
            exerciseParameterConfig: level.enabledControls.pan,
            defaultValue: 0,
         }),
         gain: createParameter({
            exerciseParameterConfig: level.enabledControls.gain,
            defaultValue: 0.6,
         }),
         effects: [],
      },

      pregain: loop.samples[id].pregain,
   }));
};

const configureInterfaceOnInitEpic = (action$) =>
   action$.pipe(
      ofType(INIT),
      switchMap(() =>
         merge(
            of(
               setInstruments([
                  {
                     source: 'Kick',
                     name: 'Kick',
                     controlType: sequencerInstrumentControlTypes.stepSequencer,
                  },
                  {
                     source: 'Snare',
                     name: 'Snare',
                     controlType: sequencerInstrumentControlTypes.stepSequencer,
                  },
                  {
                     source: 'Hat',
                     name: 'Hat',
                     controlType: sequencerInstrumentControlTypes.stepSequencer,
                  },
               ])
            ),
            of(setLoopMode()),
            of(setView({ viewType: sequencerViewTypes.mixer }))
         )
      )
   );

// loopKits other than default should be under secure url???
const maintainQueueEpic = (action$, state$) =>
   action$.pipe(
      ofType(INIT, START_NEXT_CHALLENGE),
      // await selected loopKits
      // then add some loops to queue, 2 should be loaded in background... await one of these when preparing next challenge, and keep 2 loaded always
      switchMap((action) =>
         timer(0, 100).pipe(
            filter(() => {
               const { selectedLoopKits } = state$.value.appData;
               return selectedLoopKits && !selectedLoopKits.loading;
            }),
            take(1),
            switchMap(() => {
               const currentState = state$.value;
               const level = getCurrentLevel(currentState);

               const { selectedLoopKits } = currentState.appData;

               const categories =
                  currentState.activeExercise.exerciseDocument.levels[level - 1]
                     .loopCategories;

               const queueItemsToResolve = new Array(
                  currentState.activeExercise.exerciseDocument
                     .defaultExerciseSettings.challengesPerSession -
                     currentState.activeInterface.loops.queue.length
               )
                  .fill(undefined)
                  .map(() => ({
                     loopKitId: getRandomArrayElement(selectedLoopKits.global),
                     categoryId: getRandomArrayElement(categories),
                  }));

               const nextLevelQueueItemsToResolve = [];

               const levelUpRequirements =
                  currentState.activeExercise.exerciseDocument.levels[level - 1]
                     .lvlUpRequirements;

               const { sessionsAtLevel, averageSessionScore } =
                  getSessionsAndAverageScore(
                     currentState.activeExercise,
                     currentState.activeExercise.progress.sessionHistory
                  );

               const bestPossibleAverageScore =
                  (averageSessionScore * sessionsAtLevel + 100) /
                  (sessionsAtLevel + 1);

               if (
                  action.type === START_NEXT_CHALLENGE &&
                  // Don't need to load for next level if a specific level is selected
                  currentState.activeExercise.levelSelect == null &&
                  //
                  sessionsAtLevel + 1 >= levelUpRequirements.minSessions &&
                  bestPossibleAverageScore >= levelUpRequirements.minAvgScore &&
                  currentState.activeInterface.loops.nextLevelQueue.length ===
                     0 &&
                  level <
                     currentState.activeExercise.exerciseDocument.levelCount
               ) {
                  const nextLevelCategories =
                     currentState.activeExercise.exerciseDocument.levels[level]
                        .loopCategories;

                  nextLevelQueueItemsToResolve.push({
                     loopKitId: getRandomArrayElement(selectedLoopKits.global),
                     categoryId: getRandomArrayElement(nextLevelCategories),
                  });
               }

               const countsToLoad = Object.values(
                  queueItemsToResolve
                     .concat(nextLevelQueueItemsToResolve)
                     .reduce((itemMap, currentItem) => {
                        const key = `${currentItem.loopKitId}/${currentItem.categoryId}`;
                        // this check is not necessary on init, but is on nextchallenge or whatever
                        if (
                           currentState.activeInterface.loops.categoryCounts[
                              key
                           ] == null
                        ) {
                           return {
                              ...itemMap,
                              [key]: currentItem,
                           };
                        }
                        return itemMap;
                     }, {})
               );

               return merge(
                  // get loop counts
                  from(countsToLoad).pipe(
                     map((item) => loadLoopCategoryCount(item))
                  ),
                  // resolve and add to queue after awaiting loop count
                  resolveLoopQueueItems({ state$, queueItemsToResolve }),
                  resolveLoopQueueItems({
                     state$,
                     queueItemsToResolve: nextLevelQueueItemsToResolve,
                     nextLevel: true,
                  })
               );
            })
         )
      )
   );

// wait for an item to be in queue before starting session
const prepareNextSessionEpic = (action$, state$) =>
   action$.pipe(
      ofType(PREPARE_NEXT_SESSION),
      switchMap(() =>
         timer(0, 100).pipe(
            filter(() => state$.value.activeInterface.loops.queue.length > 0),
            take(1),
            mapTo(exerciseReady())
         )
      )
   );

// on prepare challenge, update current from queue, and wait for loop to be loaded...
const prepareNextChallengeEpic = (action$, state$) =>
   action$.pipe(
      ofType(PREPARE_NEXT_CHALLENGE),
      switchMap(() =>
         merge(
            of(setCurrentLoopFromQueue()),
            action$.pipe(
               ofType(SET_CURRENT_LOOP_FROM_QUEUE),
               take(1),
               switchMap(() => timer(0, 100)),
               filter(() => {
                  const loop =
                     state$.value.activeInterface.loops.cache[
                        state$.value.activeInterface.loops.currentLoop
                     ];
                  return loop && !loop.loading;
               }),
               take(1),
               switchMap(() =>
                  merge(
                     // clean this the F up, use get level... handle module b/c this will be used in EQ too
                     of(
                        setInstruments(
                           createMixer(
                              state$.value.activeInterface.loops.cache[
                                 state$.value.activeInterface.loops.currentLoop
                              ],
                              getLevel(state$.value)
                           )
                        )
                     ),
                     of(exerciseChallengeReady())
                  )
               )
            )
         )
      )
   );

const replaceQueueWithNextLevelEpic = (action$) =>
   action$.pipe(
      ofType(SET_STAGE),
      filter((action) => action.stage === exerciseStages.levelUp),
      mapTo(replaceQueueWithNextLevel())
   );

const emulateForTutorialEpic = (action$, state$) =>
   action$.pipe(
      ofType(EMULATE_EXERCISE_FOR_TUTORIAL),
      switchMap(() => {
         const loop = state$.value.activeInterface.loops.queue[0];
         const loopPath = `${loop.loopKitId}/${loop.categoryId}/${loop.loopId}`;
         return merge(
            of(setCurrentLoop(loopPath)),
            of(
               setInstruments(
                  createMixer(
                     state$.value.activeInterface.loops.cache[loopPath],
                     getLevel(state$.value)
                  )
               )
            )
         );
      })
   );

export const mixerExerciseEpics = combineExerciseEpics(
   configureInterfaceOnInitEpic,
   maintainQueueEpic,
   prepareNextChallengeEpic,
   replaceQueueWithNextLevelEpic,
   prepareNextSessionEpic,
   emulateForTutorialEpic
);
